import React, { useState, useEffect, useCallback, useRef } from 'react'
import { mergeService } from '../services/mergeService'
import { exportMergedCompanies, getExportHistory, downloadExport } from '../services/exportService'
import {
  DuplicateGroup,
  ExportRecord,
  StatusFilter,
  MergeStats,
  GroupSummary,
  CONFIDENCE_COLORS,
  STATUS_COLORS,
} from '../types'

const FONT = 'Lexend Deca, sans-serif'

const MergeReviewDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [groups, setGroups] = useState<GroupSummary[]>([])
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null)
  const [currentGroup, setCurrentGroup] = useState<DuplicateGroup | null>(null)
  const [mergedValues, setMergedValues] = useState<Record<string, string>>({})
  const [filter, setFilter] = useState<StatusFilter>('all')
  const [stats, setStats] = useState<MergeStats>({ total: 0, pending: 0, merged: 0, skipped: 0, notDuplicates: 0 })
  const [showIdentical, setShowIdentical] = useState(false)
  const [showEmpty, setShowEmpty] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [showExportHistory, setShowExportHistory] = useState(false)
  const [exportHistory, setExportHistory] = useState<ExportRecord[]>([])
  const toastTimer = useRef<NodeJS.Timeout | null>(null)

  // Load data on mount
  useEffect(() => {
    const init = async () => {
      try {
        await mergeService.loadData()
        refreshState()
        const ids = mergeService.getFilteredGroupIds('all')
        if (ids.length > 0) {
          loadGroup(ids[0])
        }
      } catch (error) {
        console.error('Failed to load data:', error)
      } finally {
        setLoading(false)
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshState = useCallback(() => {
    setGroups(mergeService.getGroupSummaries())
    setStats(mergeService.getStats())
  }, [])

  const loadGroup = useCallback((groupId: string) => {
    const group = mergeService.getGroup(groupId)
    if (!group) return
    setCurrentGroupId(groupId)
    setCurrentGroup(group)

    const saved = mergeService.getSavedMergedValues(groupId)
    if (saved) {
      setMergedValues({ ...saved })
    } else {
      setMergedValues({ ...group.suggestedMerge })
    }
  }, [])

  const filteredIds = mergeService.getFilteredGroupIds(filter)
  const currentIndex = currentGroupId ? filteredIds.indexOf(currentGroupId) : -1

  const go = useCallback((dir: number) => {
    const newIdx = currentIndex + dir
    if (newIdx >= 0 && newIdx < filteredIds.length) {
      loadGroup(filteredIds[newIdx])
    }
  }, [currentIndex, filteredIds, loadGroup])

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(null), 2500)
  }, [])

  const handleDecide = useCallback(async (action: 'merged' | 'skipped' | 'not_duplicates') => {
    if (!currentGroupId) return

    try {
      await mergeService.decide(
        currentGroupId,
        action,
        action === 'merged' ? mergedValues : undefined
      )
      refreshState()

      const label = action === 'merged' ? 'Merged!' : action === 'skipped' ? 'Skipped' : 'Marked not duplicates'
      showToast(label)

      // Move to next in current filter
      const newFilteredIds = mergeService.getFilteredGroupIds(filter)
      if (newFilteredIds.length === 0) {
        setCurrentGroup(null)
        setCurrentGroupId(null)
        return
      }
      const nextIdx = Math.min(currentIndex, newFilteredIds.length - 1)
      loadGroup(newFilteredIds[nextIdx])
    } catch (error) {
      console.error('Failed to save decision:', error)
      showToast('Failed to save — please try again')
    }
  }, [currentGroupId, mergedValues, filter, currentIndex, refreshState, showToast, loadGroup])

  const handleExport = useCallback(async () => {
    setExporting(true)
    try {
      const result = await exportMergedCompanies(
        mergeService.getProgress(),
        mergeService.getGroups()
      )
      showToast(`Exported: ${result.newCount} records (${result.removed} removed)`)
      // Refresh export history
      const history = await getExportHistory()
      setExportHistory(history)
    } catch (error) {
      console.error('Export failed:', error)
      showToast('Export failed')
    } finally {
      setExporting(false)
    }
  }, [showToast])

  const handleShowExportHistory = useCallback(async () => {
    if (!showExportHistory) {
      const history = await getExportHistory()
      setExportHistory(history)
    }
    setShowExportHistory(!showExportHistory)
  }, [showExportHistory])

  const handleDownloadExport = useCallback(async (filePath: string) => {
    try {
      await downloadExport(filePath)
    } catch (error) {
      console.error('Download failed:', error)
      showToast('Download failed')
    }
  }, [showToast])

  const handleFilterChange = useCallback((newFilter: StatusFilter) => {
    setFilter(newFilter)
    const ids = mergeService.getFilteredGroupIds(newFilter)
    if (ids.length > 0) {
      loadGroup(ids[0])
    } else {
      setCurrentGroup(null)
      setCurrentGroupId(null)
    }
  }, [loadGroup])

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return

      if (e.key === 'ArrowLeft') go(-1)
      else if (e.key === 'ArrowRight') go(1)
      else if (e.key === 'm' || e.key === 'M') handleDecide('merged')
      else if (e.key === 's' || e.key === 'S') handleDecide('skipped')
      else if (e.key === 'n' || e.key === 'N') handleDecide('not_duplicates')
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [go, handleDecide])

  const handleMergedValueChange = useCallback((field: string, value: string) => {
    setMergedValues(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleCellClick = useCallback((field: string, value: string) => {
    setMergedValues(prev => ({ ...prev, [field]: value }))
  }, [])

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontFamily: FONT }}>
        <div style={{ textAlign: 'center', color: '#888' }}>
          <div style={{ fontSize: '18px', marginBottom: '8px' }}>Loading duplicate records...</div>
        </div>
      </div>
    )
  }

  const companyFields = mergeService.getCompanyFields()
  const progressPct = stats.total ? ((stats.merged + stats.notDuplicates + stats.skipped) / stats.total * 100) : 0
  const currentStatus = currentGroupId ? mergeService.getGroupStatus(currentGroupId) : 'pending'

  return (
    <div style={{ fontFamily: FONT, fontSize: '14px', color: '#1a1a1a', background: '#f0f2f5', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e0e0e0',
        padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '16px',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <h1 style={{ fontSize: '16px', fontWeight: 700, whiteSpace: 'nowrap', fontFamily: FONT, margin: 0 }}>
          Duplicate Merge Tool
        </h1>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <StatBadge label="Pending" count={stats.pending} bg="#fff3cd" color="#856404" />
          <StatBadge label="Merged" count={stats.merged} bg="#d4edda" color="#155724" />
          <StatBadge label="Skipped" count={stats.skipped} bg="#e2e3e5" color="#383d41" />
          <StatBadge label="Not Dups" count={stats.notDuplicates} bg="#f8d7da" color="#721c24" />
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button
            onClick={handleShowExportHistory}
            style={{
              padding: '7px 16px', border: '1px solid #e0e0e0', borderRadius: '6px',
              background: showExportHistory ? '#e8f0fe' : '#fff', color: '#333', fontSize: '13px',
              cursor: 'pointer', fontWeight: 500, fontFamily: FONT,
            }}
          >
            Export History
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            style={{
              padding: '7px 16px', border: 'none', borderRadius: '6px',
              background: '#34a853', color: '#fff', fontSize: '13px',
              cursor: exporting ? 'wait' : 'pointer', fontWeight: 600, fontFamily: FONT,
              opacity: exporting ? 0.7 : 1,
            }}
          >
            {exporting ? 'Exporting...' : 'Export Merged CSV'}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: '3px', background: '#e0e0e0' }}>
        <div style={{ height: '100%', background: '#1a73e8', width: `${progressPct}%`, transition: 'width 0.3s' }} />
      </div>

      {/* Export History Panel */}
      {showExportHistory && (
        <div style={{
          background: '#fff', borderBottom: '1px solid #e0e0e0',
          padding: '12px 20px',
        }}>
          <h3 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', fontFamily: FONT }}>
            Previous Exports
          </h3>
          {exportHistory.length === 0 ? (
            <p style={{ fontSize: '12px', color: '#888' }}>No exports yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {exportHistory.map(exp => (
                <div key={exp.id} style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px',
                  fontSize: '12px',
                }}>
                  <span style={{ color: '#333', fontWeight: 500 }}>
                    {new Date(exp.exportedAt).toLocaleString()}
                  </span>
                  <span style={{ color: '#666' }}>
                    {exp.newCount.toLocaleString()} records ({exp.removedCount} removed)
                  </span>
                  <span style={{ color: '#888' }}>
                    {exp.mergedGroups} merged, {exp.notDuplicateGroups} not dups, {exp.skippedGroups} skipped
                  </span>
                  <button
                    onClick={() => handleDownloadExport(exp.filePath)}
                    style={{
                      marginLeft: 'auto', padding: '4px 12px', border: '1px solid #e0e0e0',
                      borderRadius: '4px', background: '#fff', fontSize: '12px',
                      cursor: 'pointer', fontFamily: FONT, color: '#1a73e8', fontWeight: 500,
                    }}
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Nav */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e0e0e0',
        padding: '8px 20px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
      }}>
        <button onClick={() => go(-1)} style={navBtnStyle} title="Previous (Left arrow)">&#9664; Prev</button>
        <select
          value={currentGroupId || ''}
          onChange={(e) => loadGroup(e.target.value)}
          style={{
            padding: '6px 12px', border: '1px solid #e0e0e0', borderRadius: '6px',
            background: '#fff', fontSize: '13px', flex: 1, minWidth: '200px', maxWidth: '500px',
            fontFamily: FONT,
          }}
        >
          {filteredIds.map(gid => {
            const g = groups.find(x => x.id === gid)
            if (!g) return null
            const st = g.status !== 'pending' ? ` [${g.status}]` : ''
            const nm = g.names.filter(Boolean).slice(0, 2).join(' / ')
            return (
              <option key={gid} value={gid}>
                {gid} ({g.size}) &mdash; {nm}{st}
              </option>
            )
          })}
        </select>
        <button onClick={() => go(1)} style={navBtnStyle} title="Next (Right arrow)">Next &#9654;</button>

        <div style={{ display: 'flex', gap: '2px', marginLeft: 'auto' }}>
          {(['all', 'pending', 'merged', 'not_duplicates', 'skipped'] as StatusFilter[]).map(f => (
            <button
              key={f}
              onClick={() => handleFilterChange(f)}
              style={{
                border: 'none', padding: '5px 10px', borderRadius: '6px',
                background: filter === f ? '#e8f0fe' : 'transparent',
                fontSize: '12px',
                color: filter === f ? '#1a73e8' : '#666',
                fontWeight: filter === f ? 600 : 400,
                cursor: 'pointer', fontFamily: FONT,
              }}
            >
              {f === 'not_duplicates' ? 'Not Dups' : f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Group Info */}
      {currentGroup && (
        <div style={{
          margin: '12px 20px 0', padding: '10px 14px', background: '#fff',
          borderRadius: '8px', border: '1px solid #e0e0e0',
          display: 'flex', gap: '12px', alignItems: 'center', fontSize: '13px', flexWrap: 'wrap',
        }}>
          <strong>{currentGroup.groupId}</strong>
          {currentStatus !== 'pending' && (
            <span style={{
              fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 600,
              background: STATUS_COLORS[currentStatus]?.bg || '#e2e3e5',
              color: STATUS_COLORS[currentStatus]?.text || '#383d41',
            }}>
              {currentStatus.replace('_', ' ')}
            </span>
          )}
          <span style={{
            padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
            background: CONFIDENCE_COLORS[currentGroup.confidence]?.bg || '#f0f0f0',
            color: CONFIDENCE_COLORS[currentGroup.confidence]?.text || '#333',
          }}>
            {currentGroup.confidence}
          </span>
          <span>Match: {currentGroup.matchReasons}</span>
          <span>{currentGroup.records.length} records</span>
          <span style={{ marginLeft: 'auto', color: '#888', fontSize: '12px' }}>
            {currentIndex + 1} of {filteredIds.length}
          </span>
        </div>
      )}

      {/* Toggles */}
      <div style={{ margin: '8px 20px', display: 'flex', gap: '16px', alignItems: 'center' }}>
        <label style={{ fontSize: '12px', color: '#666', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={showIdentical}
            onChange={() => setShowIdentical(!showIdentical)}
            style={{ marginRight: '4px' }}
          />
          Show identical fields
        </label>
        <label style={{ fontSize: '12px', color: '#666', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={showEmpty}
            onChange={() => setShowEmpty(!showEmpty)}
            style={{ marginRight: '4px' }}
          />
          Show empty fields
        </label>
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#aaa' }}>
          Keys: &#8592;&#8594; navigate &middot; M merge &middot; S skip &middot; N not-dup
        </span>
      </div>

      {/* Table */}
      <div style={{ margin: '8px 20px 80px', overflowX: 'auto', flex: 1 }}>
        {currentGroup ? (
          <ComparisonTable
            group={currentGroup}
            companyFields={companyFields}
            mergedValues={mergedValues}
            showIdentical={showIdentical}
            showEmpty={showEmpty}
            onCellClick={handleCellClick}
            onMergedValueChange={handleMergedValueChange}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#888' }}>
            <h2 style={{ marginBottom: '6px', fontSize: '18px', fontFamily: FONT }}>
              {filteredIds.length === 0 ? 'No groups match this filter.' : 'No duplicate groups found.'}
            </h2>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {currentGroup && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff',
          borderTop: '1px solid #e0e0e0', padding: '10px 20px',
          display: 'flex', gap: '10px', justifyContent: 'center', zIndex: 100,
        }}>
          <button onClick={() => handleDecide('merged')} style={mergeBtnStyle}>
            Merge &amp; Next
          </button>
          <button onClick={() => handleDecide('skipped')} style={skipBtnStyle}>
            Skip
          </button>
          <button onClick={() => handleDecide('not_duplicates')} style={notDupBtnStyle}>
            Not Duplicates
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '70px', left: '50%', transform: 'translateX(-50%)',
          background: '#323232', color: '#fff', padding: '10px 24px', borderRadius: '8px',
          fontSize: '13px', zIndex: 200, fontFamily: FONT,
        }}>
          {toast}
        </div>
      )}
    </div>
  )
}

// ── Sub-components ──

const StatBadge: React.FC<{ label: string; count: number; bg: string; color: string }> = ({ label, count, bg, color }) => (
  <span style={{
    padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500,
    background: bg, color,
  }}>
    {label} {count}
  </span>
)

interface ComparisonTableProps {
  group: DuplicateGroup
  companyFields: string[]
  mergedValues: Record<string, string>
  showIdentical: boolean
  showEmpty: boolean
  onCellClick: (field: string, value: string) => void
  onMergedValueChange: (field: string, value: string) => void
}

const ComparisonTable: React.FC<ComparisonTableProps> = ({
  group, companyFields, mergedValues, showIdentical, showEmpty, onCellClick, onMergedValueChange,
}) => {
  return (
    <table style={{
      width: '100%', borderCollapse: 'collapse', background: '#fff',
      borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0',
    }}>
      <thead>
        <tr>
          <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 3 }}>Field</th>
          {group.records.map((r, ri) => {
            const name = (r.fields['Company Name'] || '').substring(0, 25)
            return (
              <th key={ri} style={thStyle}>
                Line {r.lineNumber}<br />
                <small style={{ fontWeight: 400 }}>{name}</small>
              </th>
            )
          })}
          <th style={{ ...thStyle, background: '#e8f0fe' }}>Merged Result</th>
        </tr>
      </thead>
      <tbody>
        {companyFields.map((field) => {
          const vals = group.records.map(r => (r.fields[field] || '').trim())
          const nonEmpty = vals.filter(Boolean)
          const allSame = new Set(nonEmpty).size <= 1
          const allEmpty = nonEmpty.length === 0

          if (allEmpty && !showEmpty) return null
          if (allSame && !allEmpty && !showIdentical) return null

          const isDiff = !allSame && !allEmpty
          const isNotes = field.trim() === 'Notes'

          const rowBg = isDiff ? '#fffbeb' : undefined
          const fnameBg = isDiff ? '#fff8e1' : '#f8f9fa'
          const textColor = (!isDiff && !allEmpty) ? '#aaa' : undefined

          return (
            <tr key={field}>
              <td style={{
                ...tdStyle, fontWeight: 500, background: fnameBg, whiteSpace: 'nowrap',
                minWidth: '130px', position: 'sticky', left: 0, zIndex: 1, color: textColor,
              }}>
                {field.trim()}
              </td>
              {group.records.map((r, ri) => {
                const v = vals[ri]
                const isPicked = mergedValues[field] === v && v !== ''
                const display = isNotes ? truncate(v, 200) : truncate(v, 100)
                return (
                  <td
                    key={ri}
                    onClick={() => v && onCellClick(field, v)}
                    style={{
                      ...tdStyle,
                      background: isPicked ? '#e8f0fe' : rowBg,
                      cursor: v ? 'pointer' : 'default',
                      border: isPicked ? '2px solid #1a73e8' : '2px solid transparent',
                      borderRadius: '4px',
                      maxWidth: '280px', wordBreak: 'break-word',
                      color: textColor,
                    }}
                    title={v ? 'Click to use this value' : undefined}
                  >
                    <div style={{ maxHeight: '80px', overflowY: 'auto' }}>{display}</div>
                  </td>
                )
              })}
              <td style={{ ...tdStyle, padding: '3px 6px', minWidth: '200px' }}>
                {isNotes ? (
                  <textarea
                    value={mergedValues[field] || ''}
                    onChange={(e) => onMergedValueChange(field, e.target.value)}
                    style={mergedTextareaStyle}
                  />
                ) : (
                  <input
                    value={mergedValues[field] || ''}
                    onChange={(e) => onMergedValueChange(field, e.target.value)}
                    style={mergedInputStyle}
                  />
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + '\u2026' : s
}

// ── Styles ──

const navBtnStyle: React.CSSProperties = {
  padding: '6px 12px', border: '1px solid #e0e0e0', borderRadius: '6px',
  background: '#fff', fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
}

const thStyle: React.CSSProperties = {
  background: '#f8f9fa', padding: '8px 12px', textAlign: 'left', fontSize: '12px',
  fontWeight: 600, borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap',
  position: 'sticky', top: 0, zIndex: 2, fontFamily: FONT,
}

const tdStyle: React.CSSProperties = {
  padding: '5px 10px', borderBottom: '1px solid #f0f0f0', fontSize: '13px', verticalAlign: 'top',
}

const mergedInputStyle: React.CSSProperties = {
  width: '100%', padding: '4px 8px', border: '1px solid #e0e0e0',
  borderRadius: '4px', fontSize: '13px', fontFamily: FONT,
}

const mergedTextareaStyle: React.CSSProperties = {
  width: '100%', padding: '4px 8px', border: '1px solid #e0e0e0',
  borderRadius: '4px', fontSize: '13px', fontFamily: FONT,
  minHeight: '50px', resize: 'vertical',
}

const mergeBtnStyle: React.CSSProperties = {
  padding: '10px 28px', border: 'none', borderRadius: '8px',
  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  background: '#1a73e8', color: '#fff', fontFamily: FONT,
}

const skipBtnStyle: React.CSSProperties = {
  padding: '10px 28px', border: 'none', borderRadius: '8px',
  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  background: '#f0f2f5', color: '#666', fontFamily: FONT,
}

const notDupBtnStyle: React.CSSProperties = {
  padding: '10px 28px', border: '1px solid #d93025', borderRadius: '8px',
  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
  background: '#fff', color: '#d93025', fontFamily: FONT,
}

export default MergeReviewDashboard
